import React from 'react'
import { connect } from 'react-redux'
import PropTypes from 'prop-types'
import ContentEntryDataGetter from 'layers/content/Hocs/ContentEntryDataGetter'
import withApiError from 'layers/errorHandling/apiError/component'
import {
  ERROR_TYPE_CONTENTFUL,
  SHOW_CONTENT_ON_401,
} from 'layers/errorHandling/apiError'
import {
  operations as siteOperations,
  actions as siteActions,
} from 'store/siteManager'
import { operations as lmsOperations } from 'store/lmsManager'
import { operations as licenseOperations } from 'store/licenseManager'
import { operations as userContextOperations } from 'auth/stores/userContext'
import SecondStepApi from 'lib/api/secondStepApi'
import { adaptContextV4ToLegacyContext } from 'lib/api/adapters/context'
import { Formik } from 'formik'
import { validationSchema, fieldNames, generateContext } from './data'
import ProgramRoleModalComponent from './component'
import { useUserContext } from 'layers/content/v2/hooks/useUserContext'
import { backoff } from '@cfc/exponential-backoff'

export const ProgramRoleModalContainer = ({
  clearError,
  fetchOrCreateInstructorProgramFlex,
  fetchUserContext,
  fetchUserSites,
  hasApiError,
  handleClose,
  instructorData,
  entry,
  rawUserContext,
  updateSiteUserPreferences,
  programPreferences,
}) => {
  const { yearsTeaching, teachesLessons } = programPreferences || {}
  const userContext = useUserContext()

  const { choiceContent = [] } = entry || {}
  const { answerChoice1: teachChoice1, answerChoice2: teachChoice2 } =
    choiceContent[0] || {}
  const teachOptions = {
    [teachChoice1]: true,
    [teachChoice2]: false,
  }
  const { answerChoice1: yearsChoice1 } = choiceContent[1] || {}
  const handleSave = async values => {
    // Use rawUserContext.sites for the full list of sites (userContext is filtered to selected site only)
    const allSites = rawUserContext?.sites || []
    const currentSite = userContext?.sites?.[0] || {}

    // Determine which sites to update
    const hasMultipleSites = Array.isArray(allSites) && allSites.length > 1
    const shouldApplyToAll = hasMultipleSites
      ? values.applyToAll !== false
      : false

    const teachesLessonsLocal = teachOptions?.[values.teachesLessonsAnswer]

    if (!shouldApplyToAll) {
      // Preserve original single-site behavior: update the current site regardless of claims
      const { claims = [], siteId } = currentSite || {}
      const isAdmin = claims.some(claim => claim?.toLowerCase() === 'admin')
      const programTrack =
        teachesLessonsLocal || isAdmin ? 'Instructor' : 'Staff'

      const newSitePreferences = {
        k5Digital: {
          userProgramPreferences: {
            teachesLessons: teachesLessonsLocal,
            yearsTeaching: values.yearsTeaching,
            programTrack,
          },
        },
        mspDigital: {
          userProgramPreferences: {
            teachesLessons: teachesLessonsLocal,
            yearsTeaching: values.yearsTeaching,
            programTrack,
          },
        },
      }

      await updateSiteUserPreferences(siteId, newSitePreferences)

      if (
        !instructorData?.K5SelDigital?.educator?.length > 0 ||
        !instructorData?.MspDigital?.educator?.length > 0
      ) {
        const k5Context = generateContext(
          'k-5_training_tier1',
          siteId,
          claims,
          programTrack,
          'k5SelDigital',
        )
        const mspContext = generateContext(
          '6-8_training_tier1',
          siteId,
          claims,
          programTrack,
          'mspDigital',
        )

        await fetchOrCreateInstructorProgramFlex(siteId, k5Context)
        await fetchOrCreateInstructorProgramFlex(siteId, mspContext)
      }
    } else {
      // Apply to all eligible sites: must have K8 and teacher or admin claim; update only present programs
      const eligibleSites = allSites.filter(site => {
        const { claims = [], programs = {} } = site || {}
        const hasK5 = !!programs?.k5SelDigital
        const hasMs = !!programs?.mspDigital
        const hasK8 = hasK5 || hasMs
        const hasClaim = claims.some(
          claim =>
            claim?.toLowerCase() === 'teacher' ||
            claim?.toLowerCase() === 'admin',
        )
        return hasK8 && hasClaim
      })

      const perSiteOps = eligibleSites.map(site => async () => {
        const { claims = [], siteId, programs = {} } = site || {}
        const isAdmin = claims.some(claim => claim?.toLowerCase() === 'admin')
        const programTrack =
          teachesLessonsLocal || isAdmin ? 'Instructor' : 'Staff'

        const newSitePreferences = {}
        if (programs?.k5SelDigital) {
          newSitePreferences.k5Digital = {
            userProgramPreferences: {
              teachesLessons: teachesLessonsLocal,
              yearsTeaching: values.yearsTeaching,
              programTrack,
            },
          }
        }
        if (programs?.mspDigital) {
          newSitePreferences.mspDigital = {
            userProgramPreferences: {
              teachesLessons: teachesLessonsLocal,
              yearsTeaching: values.yearsTeaching,
              programTrack,
            },
          }
        }

        await backoff(
          () => updateSiteUserPreferences(siteId, newSitePreferences),
          3,
        )

        const needsK5 =
          programs?.k5SelDigital &&
          !instructorData?.K5SelDigital?.educator?.length > 0
        const needsMs =
          programs?.mspDigital &&
          !instructorData?.MspDigital?.educator?.length > 0

        if (needsK5) {
          const k5Context = generateContext(
            'k-5_training_tier1',
            siteId,
            claims,
            programTrack,
            'k5SelDigital',
          )
          await backoff(
            () => fetchOrCreateInstructorProgramFlex(siteId, k5Context),
            3,
          )
        }
        if (needsMs) {
          const mspContext = generateContext(
            '6-8_training_tier1',
            siteId,
            claims,
            programTrack,
            'mspDigital',
          )
          await backoff(
            () => fetchOrCreateInstructorProgramFlex(siteId, mspContext),
            3,
          )
        }
      })

      for (const op of perSiteOps) {
        await op()
      }
    }

    // Single refresh after all work is done
    await fetchUserSites()
    // Drive AUTH to correct API with optional adapter
    const contextBase =
      SecondStepApi.baseApiAddressForContext || SecondStepApi.baseApiAddress
    const contextPath = contextBase.endsWith('/v4/') ? 'context/me' : 'context'
    await fetchUserContext(contextBase, {
      contextPath,
      adaptResponse:
        contextPath === 'context/me'
          ? adaptContextV4ToLegacyContext
          : undefined,
    })
  }

  const closeModal = () => {
    clearError('sitePreferences')
    handleClose()
  }

  const hasTeachesLessons =
    teachesLessons !== undefined && teachesLessons !== null
  const initialValues = {
    teachesLessonsAnswer: hasTeachesLessons
      ? teachesLessons
        ? teachChoice1
        : teachChoice2
      : teachChoice1,
    yearsTeaching: yearsTeaching || yearsChoice1,
    applyToAll: true,
  }

  const showApplyToAll = (rawUserContext?.sites || []).length > 1

  return entry ? (
    <Formik
      initialTouched={{
        teachesLessonsAnswer: !!teachesLessons,
        yearsTeaching: !!yearsTeaching,
      }}
      initialValues={initialValues}
      validationSchema={validationSchema}
    >
      {({
        values,
        errors,
        touched,
        setFieldValue,
        setFieldTouched,
        setSubmitting,
        setTouched,
        isSubmitting,
        isValid,
      }) => (
        <ProgramRoleModalComponent
          entry={entry}
          errors={errors}
          handleChange={(name, value) => {
            setFieldTouched(name)
            setFieldValue(name, value)
          }}
          handleClose={closeModal}
          handleNextButtonClick={async () => {
            const isValidSync = validationSchema.isValidSync(values)
            if (!isValidSync) {
              const touchedFields = fieldNames.reduce((acc, name) => {
                acc[name] = true
                return acc
              }, {})
              await setTouched(touchedFields)
              return
            }
            setSubmitting(true)
            await handleSave(values)
            setSubmitting(false)
            closeModal()
          }}
          hasApiError={hasApiError}
          isSubmitting={isSubmitting}
          isValid={isValid}
          showApplyToAll={showApplyToAll}
          touched={touched}
          values={values}
        />
      )}
    </Formik>
  ) : null
}

const mapStateToProps = state => {
  const { sitePreferences: sitePreferencesAPIResults } = state.siteManager
  const { error } = sitePreferencesAPIResults

  return {
    hasApiError: !!error,
    rawUserContext: state.userContextManager,
  }
}

const mapDispatchToProps = {
  updateSiteUserPreferences: siteOperations.updateSiteUserPreferences,
  fetchUserSites: licenseOperations.fetchUserSites,
  fetchOrCreateInstructorProgramFlex:
    lmsOperations.fetchOrCreateInstructorProgramFlex,
  fetchUserContext: userContextOperations.fetchUserContextReduxConnect,
  clearError: siteActions.clearError,
}

ProgramRoleModalContainer.propTypes = {
  clearError: PropTypes.func,
  entry: PropTypes.object,
  fetchOrCreateInstructorProgramFlex: PropTypes.func,
  fetchUserContext: PropTypes.func,
  fetchUserSites: PropTypes.func,
  handleClose: PropTypes.func,
  hasApiError: PropTypes.bool,
  instructorData: PropTypes.object,
  programPreferences: PropTypes.object,
  rawUserContext: PropTypes.object,
  siteId: PropTypes.number,
  teachesLessons: PropTypes.bool,
  updateSiteUserPreferences: PropTypes.func,
  yearsTeaching: PropTypes.string,
}

const mapper = entry => {
  return {
    entry,
  }
}

const options = {
  entryId: '3fxndewvV5bkqTkLuTcMW4',
  include: 3,
  mapper,
  spread: true,
}

// TODO: use contentful data from K8ProgramStatusRouter instead of this
export default ContentEntryDataGetter(
  withApiError(
    ProgramRoleModalContainer,
    [ERROR_TYPE_CONTENTFUL],
    [SHOW_CONTENT_ON_401],
  ),
  options,
  connect(mapStateToProps, mapDispatchToProps),
)
